import { useRef } from 'react';
import { Flex } from '@servicetitan/anvil2';
import { ErrorBoundary } from '@servicetitan/financing-utils-shared';
import { ApplicationData, ApplicationDataStore } from '@servicetitan/financing-application-shared';
import {
    FinancingInformationContainer,
    PlansContainer,
    AutoProviderLogo,
    PreApprovalBanner,
} from '@servicetitan/financing-plans-shared';
import { plansPanel, wrapper } from '../workflow/ApplicationWorkflow.module.scss';

interface SelectPlanStepProps {
    onPlanSelected?: () => void;
    // The "See If I Pre-qualify" (not-me) action. Defaults to onPlanSelected; apps that track
    // pre-approval activity pass a distinct handler so a not-me close isn't logged as a decline.
    onPreQualify?: () => void;
    canChangeProvider?: boolean;
}

// A "not me" click means the on-file offer belongs to someone else, so the application form must
// not prefill with their details. The service address isn't applicant-specific, so it's kept.
const UNKNOWN_APPLICANT: ApplicationData['externalApplication'] = {
    firstName: '',
    lastName: '',
    email: '',
    primaryPhone: '',
    ssnLast4: '',
};

export const SelectPlanStep = (props: SelectPlanStepProps) => {
    const { canChangeProvider = false, onPlanSelected, onPreQualify = onPlanSelected } = props;
    const { applicationData, setApplicationData } = ApplicationDataStore.useStore();
    // Holds the customer's own details across a not-me click so a later regular plan selection can
    // restore them instead of leaving the form blank or holding a stranger's edits.
    const customerApplicantRef = useRef<ApplicationData['externalApplication'] | null>(null);

    const { firstName, lastName } = applicationData.applicant.primaryApplicant ?? {};
    const customerName = [firstName, lastName].filter(Boolean).join(' ') || undefined;

    const handlePreQualify = () => {
        customerApplicantRef.current ??= applicationData.externalApplication;
        setApplicationData(prev => ({
            ...prev,
            externalApplication: {
                address: prev.externalApplication.address,
                ...UNKNOWN_APPLICANT,
            },
        }));
        onPreQualify?.();
    };

    const handlePlanSelected = () => {
        if (customerApplicantRef.current) {
            const customerApplicant = customerApplicantRef.current;
            customerApplicantRef.current = null;
            setApplicationData(prev => ({ ...prev, externalApplication: customerApplicant }));
        }
        onPlanSelected?.();
    };

    return (
        <div className={plansPanel} style={{ width: '100%' }}>
            <div className={wrapper}>
                <Flex direction="column" gap="4">
                    <ErrorBoundary actionCausingError="loading available plans">
                        {canChangeProvider ? (
                            <FinancingInformationContainer
                                showProviderSelection
                                amountRangeEnabled
                            />
                        ) : (
                            <>
                                <AutoProviderLogo />
                                <FinancingInformationContainer amountRangeEnabled />
                            </>
                        )}
                        <PreApprovalBanner
                            customerName={customerName}
                            onPreQualify={handlePreQualify}
                        />
                        <PlansContainer onPlanSelected={handlePlanSelected} amountRangeEnabled />
                    </ErrorBoundary>
                </Flex>
            </div>
        </div>
    );
};
